import React, { useState, useEffect, useRef } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../contexts/firebase";
import { useAuth } from "../contexts/AuthContext";
import {
  signInWithEmailAndPassword,
  updatePassword,
} from "firebase/auth";
import Toast from "../components/Toast";
import { Link } from "react-router-dom";

export default function Profile() {
  const { currentUser } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", dob: "",
    currentPassword: "", newPassword: "", confirmNewPassword: "",
  });
  const [errors, setErrors] = useState({});
  const original = useRef(null);

  // Responsive background toggle
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load Firestore data once
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          const initial = {
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            dob: data.dob || "",
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          };
          setForm(initial);
          original.current = initial;
        }
      } catch (err) {
        console.error(err);
        setToast({ message: "Failed to load profile.", type: "error" });
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "Required";
    if (!form.lastName.trim()) errs.lastName = "Required";
    if (!form.dob) errs.dob = "Required";

    // Only validate password section if any field is non-empty
    const { currentPassword, newPassword, confirmNewPassword } = form;
    if (currentPassword || newPassword || confirmNewPassword) {
      if (!currentPassword) errs.currentPassword = "Enter current password";
      const pwdRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
      if (!pwdRegex.test(newPassword))
        errs.newPassword =
          "New password must be ≥8 chars with upper, lower, number & special char";
      if (newPassword !== confirmNewPassword)
        errs.confirmNewPassword = "Does not match";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setToast({ message: "", type: "" });
    if (!validate()) return;

    // Dirty check
    const profDirty =
      form.firstName !== original.current.firstName ||
      form.lastName !== original.current.lastName ||
      form.dob !== original.current.dob;
    const pwdDirty =
      !!form.currentPassword ||
      !!form.newPassword ||
      !!form.confirmNewPassword;
    if (!profDirty && !pwdDirty) {
      setToast({ message: "No changes to save.", type: "info" });
      return;
    }

    setSaving(true);
    try {
      // Update profile fields
      if (profDirty) {
        await updateDoc(doc(db, "users", currentUser.uid), {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          dob: form.dob,
        });
        // refresh original values
        original.current.firstName = form.firstName;
        original.current.lastName = form.lastName;
        original.current.dob = form.dob;
      }

      // Handle password change
      if (pwdDirty) {
        await signInWithEmailAndPassword(
          auth,
          form.email,
          form.currentPassword
        );
        await updatePassword(auth.currentUser, form.newPassword);
      }

      setToast({ message: "Profile updated!", type: "success" });
      // clear only password fields
      setForm((f) => ({
        ...f,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      }));
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setToast({
        message: err.message.includes("wrong-password")
          ? "Current password incorrect."
          : "Failed to save changes.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // revert to original
    setForm(original.current);
    setErrors({});
    setToast({ message: "", type: "" });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Loading…
      </div>
    );
  }

  const bg = isMobile
    ? "https://geneartai.com/images/mobile1.png"
    : "https://geneartai.com/images/image1.png";

  const FieldError = ({ field }) =>
    errors[field] ? (
      <p className="text-red-500 text-sm mt-1">{errors[field]}</p>
    ) : null;

  return (
    <div className="relative min-h-screen font-sans text-white overflow-x-hidden">
      {/* Background */}
      <div
        className="fixed inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: `url('${bg}')`,
          backgroundAttachment: isMobile ? "scroll" : "fixed",
        }}
      >
        <div className="absolute inset-0"/>
      </div>

      {/* Form */}
      <div className="relative z-10 flex flex-col min-h-screen px-4 md:px-8 pt-20 md:pt-32">
        <div className="w-full max-w-xl bg-black/20 backdrop-blur-md rounded-2xl shadow-2xl p-6 md:p-8 mx-auto">
          {/* Edit / Cancel / Save Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl md:text-3xl font-extrabold text-center flex-grow">
              My Profile
            </h1>
            {isEditing ? (
              <button
                onClick={handleCancel}
                className="ml-4 text-sm text-gray-300 hover:text-white"
              >
                Cancel
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="ml-4 text-sm text-white px-3 py-1 border border-white rounded hover:bg-white hover:text-black transition"
              >
                Edit
              </button>
            )}
          </div>

          {toast.message && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ message: "", type: "" })}
              duration={4000}
            />
          )}

          <form onSubmit={handleSave} className="space-y-6" autoComplete="off">
            {/* First & Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-base font-semibold mb-2 text-gray-200">
                  First Name
                </label>
                <input
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 rounded-lg border ${isEditing
                    ? "bg-black/20 border-gray-600 focus:ring-2 focus:ring-[#166CB0]"
                    : "bg-gray-800 border-gray-700"
                    } text-white`}
                />
                <FieldError field="firstName" />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-base font-semibold mb-2 text-gray-200">
                  Last Name
                </label>
                <input
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 rounded-lg border ${isEditing
                    ? "bg-black/20 border-gray-600 focus:ring-2 focus:ring-[#166CB0]"
                    : "bg-gray-800 border-gray-700"
                    } text-white`}
                />
                <FieldError field="lastName" />
              </div>
            </div>

            {/* Email (always readonly) */}
            <div>
              <label className="block text-base font-semibold mb-2 text-gray-200">
                Email
              </label>
              <input
                name="email"
                value={form.email}
                readOnly
                className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-400"
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-base font-semibold mb-2 text-gray-200">
                Date of Birth
              </label>
              <input
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-3 py-2 rounded-lg border ${isEditing
                  ? "bg-black/20 border-gray-600 focus:ring-2 focus:ring-[#166CB0]"
                  : "bg-gray-800 border-gray-700"
                  } text-white`}
              />
              <FieldError field="dob" />
            </div>

            {/* Password Section (only editable in edit mode) */}
            {isEditing && (
              <div className="border-t border-gray-600 pt-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-200">
                  Change Password
                </h2>
                <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
                  {/* Current Password */}
                  <div>
                    <label className="block text-base font-semibold mb-2 text-gray-200">
                      Current Password
                    </label>
                    <input
                      name="currentPassword"
                      type="password"
                      value={form.currentPassword}
                      onChange={handleChange}
                      placeholder="Enter current password"
                      autoComplete="new-password"  // Prevents autofill
                      className="w-full px-3 py-2 rounded-lg bg-black/20 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#166CB0]"
                    />
                    <FieldError field="currentPassword" />
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="block text-base font-semibold mb-2 text-gray-200">
                      New Password
                    </label>
                    <input
                      name="newPassword"
                      type="password"
                      value={form.newPassword}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      className="w-full px-3 py-2 rounded-lg bg-black/20 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#166CB0]"
                    />
                    <FieldError field="newPassword" />
                  </div>

                  {/* Confirm New Password */}
                  <div className="md:col-span-2">
                    <label className="block text-base font-semibold mb-2 text-gray-200">
                      Confirm New Password
                    </label>
                    <input
                      name="confirmNewPassword"
                      type="password"
                      value={form.confirmNewPassword}
                      onChange={handleChange}
                      placeholder="Confirm new password"
                      className="w-full px-3 py-2 rounded-lg bg-black/20 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#166CB0]"
                    />
                    <FieldError field="confirmNewPassword" />
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            {isEditing && (
              <button
                type="submit"
                disabled={saving}
                className={`w-full py-3 rounded-full font-semibold text-lg flex justify-center items-center gap-2 ${saving
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-white text-[#166CB0] hover:bg-gray-100"
                  } transition duration-300 shadow-md hover:shadow-lg`}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            )}
          </form>
        </div>
        {/* Footer */}
        <footer className="text-center text-xs text-gray-300 pb-3 mt-auto">
          <div>© 2025 GeneArtAI — All Rights Reserved.</div>
          <div className="mt-2">
            <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">
              Privacy Policy
            </Link>{" "}
            |{" "}
            <Link to="/terms-of-service" className="underline hover:text-white">
              Terms of Service
            </Link>
          </div>
        </footer>

        {/* Spacer for bottom ad */}
        <div className="block sm:hidden h-20" />
      </div>
    </div>
  );
}
